class OoStoreLocator {
  constructor(root) {
    this.root = root;
    this.map = null;
    this.markers = [];
    this.stores = [];
    this.allStores = [];
    this.selectedKey = '';
    this.isLoading = true;
    this.searchMarker = null;

    this.mapEl = root.querySelector('.oo-points-of-sale__map');
    this.listEl = root.querySelector('.oo-points-of-sale__list');
    this.loaderEl = root.querySelector('.oo-points-of-sale__loader');
    this.searchInput = root.querySelector('.oo-points-of-sale__search-input');
    this.searchBtn = root.querySelector('.oo-points-of-sale__search-button');

    this.markerDefaultUrl = root.dataset.markerDefault;
    this.markerSelectedUrl = root.dataset.markerSelected;

    const sizeParts = (root.dataset.markerSize || '44,44').split(',').map((v) => parseInt(v, 10));
    const anchorParts = (root.dataset.markerAnchor || '22,44').split(',').map((v) => parseInt(v, 10));
    this.markerIconSize = [sizeParts[0] || 44, sizeParts[1] || 44];
    this.markerIconAnchor = [anchorParts[0] || 22, anchorParts[1] || 44];

    this.icons = { default: null, selected: null };

    this.parseStores();
    this.bindEvents();
    this.boot();
  }

  async boot() {
    try {
      await this.resolveMissingCoordinates();
      this.refreshMappableStores();
      this.waitForLeaflet(() => this.initMap());
    } catch (error) {
      console.error('OO Store Locator: opstarten mislukt', error);
      this.renderList();
      this.setLoading(false);
    }
  }

  parseStores() {
    const dataEl = this.root.querySelector('[data-oo-store-locator-data]');
    if (!dataEl) {
      console.warn('OO Store Locator: geen locatie-data gevonden.');
      return;
    }

    try {
      this.allStores = JSON.parse(dataEl.textContent) || [];
    } catch (error) {
      console.error('OO Store Locator: invalid store data', error);
      this.allStores = [];
    }

    this.allStores = this.allStores.map((store) => this.normalizeStore(store));
    this.refreshMappableStores();
  }

  parseCoord(value) {
    if (value == null || value === '') return null;
    const num = parseFloat(String(value).trim().replace(',', '.'));
    return Number.isNaN(num) ? null : num;
  }

  normalizeStore(store) {
    const lat = this.parseCoord(store.lat);
    const lng = this.parseCoord(store.lng);
    const hasCoordinates = lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng);
    const searchQuery = store.searchQuery || store.address || store.postcode || '';

    return {
      ...store,
      lat: hasCoordinates ? lat : null,
      lng: hasCoordinates ? lng : null,
      hasCoordinates,
      searchQuery: searchQuery.trim(),
    };
  }

  refreshMappableStores() {
    this.stores = this.allStores.filter((store) => store.hasCoordinates);
    if (this.selectedKey && !this.stores.some((s) => this.getStoreKey(s) === this.selectedKey)) {
      this.selectedKey = '';
    }
  }

  normalizeSearchQuery(query) {
    const trimmed = query.trim().replace(/\s+/g, ' ');
    const postcodeMatch = trimmed.match(/^(\d{4})\s*([a-zA-Z]{2})$/i);
    if (postcodeMatch) {
      return `${postcodeMatch[1]} ${postcodeMatch[2].toUpperCase()}, Nederland`;
    }
    const postcodeCompact = trimmed.match(/^(\d{4})([a-zA-Z]{2})$/i);
    if (postcodeCompact) {
      return `${postcodeCompact[1]} ${postcodeCompact[2].toUpperCase()}, Nederland`;
    }
    if (!/nederland|netherlands|\bnl\b/i.test(trimmed)) {
      return `${trimmed}, Nederland`;
    }
    return trimmed;
  }

  isDutchPostcode(query) {
    return /^(\d{4})\s*([a-zA-Z]{2})$/i.test(query.trim()) || /^(\d{4})([a-zA-Z]{2})$/i.test(query.trim());
  }

  async geocodePostcodeNL(query) {
    const match =
      query.trim().match(/^(\d{4})\s*([a-zA-Z]{2})$/i) || query.trim().match(/^(\d{4})([a-zA-Z]{2})$/i);
    if (!match) return null;

    const postalcode = `${match[1]}${match[2].toUpperCase()}`;
    const url = new URL('https://nominatim.openstreetmap.org/search');
    url.searchParams.set('format', 'json');
    url.searchParams.set('postalcode', postalcode);
    url.searchParams.set('country', 'Netherlands');
    url.searchParams.set('limit', '1');

    const response = await fetch(url.toString(), {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (!Array.isArray(data) || !data[0]) return null;

    return {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon),
    };
  }

  async geocodePhoton(query) {
    const url = new URL('https://photon.komoot.io/api/');
    url.searchParams.set('q', query);
    url.searchParams.set('limit', '1');
    url.searchParams.set('lang', 'nl');

    const response = await fetch(url.toString());
    if (!response.ok) return null;

    const data = await response.json();
    const feature = data?.features?.[0];
    if (!feature?.geometry?.coordinates) return null;

    const [lng, lat] = feature.geometry.coordinates;
    return { lat: parseFloat(lat), lng: parseFloat(lng) };
  }

  async geocodeNominatim(query) {
    const url = new URL('https://nominatim.openstreetmap.org/search');
    url.searchParams.set('format', 'json');
    url.searchParams.set('q', query);
    url.searchParams.set('countrycodes', 'nl');
    url.searchParams.set('limit', '1');

    const response = await fetch(url.toString(), {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (!Array.isArray(data) || !data[0]) return null;

    return {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon),
    };
  }

  async geocodeQuery(query) {
    const trimmed = query.trim();
    if (!trimmed) return null;

    if (this.isDutchPostcode(trimmed)) {
      const postcodeResult = await this.geocodePostcodeNL(trimmed);
      if (postcodeResult) return postcodeResult;
    }

    const normalized = this.normalizeSearchQuery(trimmed);
    const photonResult = await this.geocodePhoton(normalized);
    if (photonResult) return photonResult;

    return this.geocodeNominatim(normalized);
  }

  delay(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  async resolveMissingCoordinates() {
    for (const store of this.allStores) {
      if (store.hasCoordinates || !store.searchQuery) continue;

      const coords = await this.geocodeQuery(store.searchQuery);
      if (coords) {
        store.lat = coords.lat;
        store.lng = coords.lng;
        store.hasCoordinates = true;
      }

      await this.delay(350);
    }
  }

  getRouteUrl(store) {
    return `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`;
  }

  setSearchLoading(isLoading) {
    if (!this.searchBtn) return;
    this.searchBtn.disabled = isLoading;
    this.searchBtn.classList.toggle('is-loading', isLoading);
    this.searchBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  getStoreKey(store) {
    return store.id || store.name;
  }

  bindEvents() {
    this.searchBtn?.addEventListener('click', () => this.findNearestStore());
    this.searchInput?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.findNearestStore();
      }
    });
  }

  waitForLeaflet(callback) {
    if (typeof L !== 'undefined') {
      callback();
      return;
    }

    let attempts = 0;
    const maxAttempts = 100;

    const interval = window.setInterval(() => {
      attempts += 1;

      if (typeof L !== 'undefined') {
        window.clearInterval(interval);
        callback();
        return;
      }

      if (attempts >= maxAttempts) {
        window.clearInterval(interval);
        console.error('OO Store Locator: Leaflet kon niet geladen worden.');
        this.renderList();
        this.setLoading(false);
      }
    }, 100);
  }

  initMap() {
    try {
      if (!this.mapEl || typeof L === 'undefined') {
        return;
      }

      if (this.map) {
        this.map.remove();
        this.map = null;
      }
      this.searchMarker = null;

      this.map = L.map(this.mapEl, {
        minZoom: 6,
        maxZoom: 18,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(this.map);

      this.createMarkerIcons();

      this.stores.forEach((store) => this.addStoreMarker(store));

      window.requestAnimationFrame(() => {
        this.map?.invalidateSize();
        this.fitNetherlandsView();
      });
    } catch (error) {
      console.error('OO Store Locator: kaart initialisatie mislukt', error);
    } finally {
      this.renderList();
      this.setLoading(false);
    }
  }

  buildLeafletIcon(iconUrl) {
    return L.icon({
      iconUrl,
      iconRetinaUrl: iconUrl,
      iconSize: this.markerIconSize,
      iconAnchor: this.markerIconAnchor,
      popupAnchor: [0, -this.markerIconAnchor[1]],
      shadowUrl: '',
      shadowSize: [0, 0],
      shadowAnchor: [0, 0],
      className: 'oo-points-of-sale__marker-icon',
    });
  }

  createMarkerIcons() {
    const defaultUrl = this.markerDefaultUrl?.trim();
    const selectedUrl = this.markerSelectedUrl?.trim();

    if (!defaultUrl || !selectedUrl) {
      console.warn('OO Store Locator: pin-icoon URL ontbreekt.');
      this.icons.default = null;
      this.icons.selected = null;
      return;
    }

    try {
      this.icons.default = this.buildLeafletIcon(defaultUrl);
      this.icons.selected = this.buildLeafletIcon(selectedUrl);
    } catch (error) {
      console.warn('OO Store Locator: custom icons mislukt, standaard pins gebruikt', error);
      this.icons.default = null;
      this.icons.selected = null;
    }
  }

  getNetherlandsBounds() {
    return L.latLngBounds(
      [50.35, 3.05],
      [53.7, 7.45]
    );
  }

  fitNetherlandsView() {
    if (!this.map) return;

    this.map.fitBounds(this.getNetherlandsBounds(), {
      padding: [24, 24],
      maxZoom: 8,
      animate: false,
    });
  }

  addStoreMarker(store) {
    if (!this.map) return;

    const markerOptions = {};
    if (this.icons.default) {
      markerOptions.icon = this.icons.default;
    }

    const marker = L.marker([store.lat, store.lng], markerOptions).addTo(this.map);

    marker.on('click', () => this.selectStore(store));

    this.markers.push({ marker, key: this.getStoreKey(store) });
  }

  renderList() {
    if (!this.listEl) return;

    this.listEl.innerHTML = '';

    if (this.allStores.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'oo-points-of-sale__empty';
      empty.textContent = this.root.dataset.emptyText || 'Geen verkooppunten gevonden.';
      this.listEl.appendChild(empty);
      return;
    }

    const listStores = this.stores.length > 0 ? this.stores : this.allStores;

    listStores.forEach((store) => {
      const card = document.createElement('article');
      card.className = 'oo-points-of-sale__card';
      const storeKey = this.getStoreKey(store);
      const isMappable = store.hasCoordinates;

      if (isMappable && storeKey === this.selectedKey) {
        card.classList.add('oo-points-of-sale__card--active');
      }

      if (!isMappable) {
        card.classList.add('oo-points-of-sale__card--disabled');
      }

      card.dataset.storeKey = storeKey;

      const info = document.createElement('div');
      info.className = 'oo-points-of-sale__card-info';

      const name = document.createElement('h3');
      name.className = 'oo-points-of-sale__card-name';
      name.textContent = store.name;

      const address = document.createElement('p');
      address.className = 'oo-points-of-sale__card-address';
      address.textContent = store.address;

      info.append(name, address);

      if (store.products) {
        const products = document.createElement('p');
        products.className = 'oo-points-of-sale__card-products';
        products.textContent = store.products;
        info.insertBefore(products, address);
      }

      if (!isMappable) {
        const hint = document.createElement('p');
        hint.className = 'oo-points-of-sale__card-hint';
        hint.textContent = 'Vul postcode en adres in (of latitude/longitude) om op de kaart te tonen.';
        info.appendChild(hint);
      }

      card.append(info);

      if (isMappable) {
        const route = document.createElement('a');
        route.className = 'oo-points-of-sale__card-route';
        route.href = this.getRouteUrl(store);
        route.target = '_blank';
        route.rel = 'noopener noreferrer';
        route.innerHTML = `<span>${this.root.dataset.routeLabel || 'Route'}</span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        card.append(route);

        card.addEventListener('click', (event) => {
          if (event.target.closest('a')) return;
          this.selectStore(store);
        });
      }

      this.listEl.appendChild(card);
    });
  }

  selectStore(store, scrollList = true, zoomMap = true) {
    if (!store.hasCoordinates) return;

    this.selectedKey = this.getStoreKey(store);

    if (this.map && zoomMap) {
      this.map.setView([store.lat, store.lng], 14);
    }

    this.markers.forEach(({ marker, key }) => {
      const isActive = key === this.selectedKey;
      if (this.icons.selected && this.icons.default) {
        marker.setIcon(isActive ? this.icons.selected : this.icons.default);
      }
      marker.setZIndexOffset(isActive ? 1000 : 0);
    });

    this.listEl?.querySelectorAll('.oo-points-of-sale__card').forEach((card) => {
      card.classList.toggle('oo-points-of-sale__card--active', card.dataset.storeKey === this.selectedKey);
    });

    if (scrollList) {
      const container = this.listEl;
      const activeCard = container?.querySelector(`[data-store-key="${CSS.escape(this.selectedKey)}"]`);
      if (container && activeCard) {
        const offset =
          activeCard.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop -
          container.clientHeight / 2 +
          activeCard.clientHeight / 2;
        container.scrollTo({ top: offset, behavior: 'smooth' });
      }
    }
  }

  async findNearestStore() {
    const query = this.searchInput?.value?.trim();
    if (!query) {
      window.alert(this.root.dataset.searchEmptyText || 'Voer een locatie in.');
      return;
    }

    await this.resolveMissingCoordinates();
    this.refreshMappableStores();

    const mappableStores = this.allStores.filter((s) => s.hasCoordinates);
    if (mappableStores.length === 0) {
      window.alert(
        'Geen locaties met coördinaten gevonden. Vul postcode en adres in, of latitude/longitude per verkooppunt.'
      );
      return;
    }

    this.setSearchLoading(true);

    try {
      const userCoords = await this.geocodeQuery(query);
      if (!userCoords) {
        window.alert(this.root.dataset.searchNotFoundText || 'Locatie niet gevonden.');
        return;
      }

      const nearestStore = this.sortStoresByDistance(userCoords.lat, userCoords.lng);
      if (!nearestStore) {
        window.alert(this.root.dataset.searchNotFoundText || 'Locatie niet gevonden.');
        return;
      }

      this.showSearchLocation(userCoords);
      this.selectStore(nearestStore, true, false);
      this.fitSearchResultView(userCoords, nearestStore);
    } catch (error) {
      console.error('OO Store Locator: geocode error', error);
      window.alert(this.root.dataset.searchNotFoundText || 'Locatie niet gevonden.');
    } finally {
      this.setSearchLoading(false);
    }
  }

  showSearchLocation(coords) {
    if (!this.map || typeof L === 'undefined') return;

    if (this.searchMarker) {
      this.map.removeLayer(this.searchMarker);
      this.searchMarker = null;
    }

    this.searchMarker = L.circleMarker([coords.lat, coords.lng], {
      radius: 7,
      color: '#575757',
      weight: 2,
      fillColor: '#fff',
      fillOpacity: 1,
    }).addTo(this.map);
  }

  fitSearchResultView(userCoords, store) {
    if (!this.map || typeof L === 'undefined') return;

    const bounds = L.latLngBounds(
      [userCoords.lat, userCoords.lng],
      [store.lat, store.lng]
    );

    this.map.fitBounds(bounds, {
      padding: [48, 48],
      maxZoom: 13,
      animate: true,
    });
  }

  sortStoresByDistance(userLat, userLng) {
    this.stores = this.allStores
      .filter((store) => store.hasCoordinates)
      .map((store) => ({
        ...store,
        distance: this.haversine(userLat, userLng, store.lat, store.lng),
      }))
      .sort((a, b) => a.distance - b.distance);

    this.markers.forEach(({ marker }) => this.map?.removeLayer(marker));
    this.markers = [];
    this.stores.forEach((store) => this.addStoreMarker(store));

    this.renderList();
    return this.stores[0] || null;
  }

  haversine(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  }

  setLoading(isLoading) {
    this.isLoading = isLoading;
    if (this.loaderEl) {
      this.loaderEl.classList.toggle('is-active', isLoading);
      this.loaderEl.hidden = isLoading;
      this.loaderEl.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
      this.loaderEl.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
    this.root.classList.toggle('oo-points-of-sale--loading', isLoading);
  }
}

let ooStoreLocatorBooted = false;

function initOoStoreLocators() {
  document.querySelectorAll('[data-oo-store-locator]').forEach((root) => {
    if (root._ooStoreLocator) {
      root._ooStoreLocator.map?.remove();
      root._ooStoreLocator = null;
    }
    root._ooStoreLocator = new OoStoreLocator(root);
  });
}

function bootStoreLocator() {
  if (ooStoreLocatorBooted) return;
  ooStoreLocatorBooted = true;

  const runInit = () => initOoStoreLocators();

  if (typeof L !== 'undefined') {
    runInit();
    return;
  }

  const leafletScript = document.querySelector('script[src*="leaflet"]');
  if (leafletScript) {
    leafletScript.addEventListener('load', runInit, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInit, { once: true });
  } else {
    runInit();
  }
}

bootStoreLocator();
document.addEventListener('shopify:section:load', initOoStoreLocators);

if (!customElements.get('oo-store-locator')) {
  customElements.define('oo-store-locator', class OoStoreLocatorElement extends HTMLElement {});
}
